import { isNative } from '@/capacitor'
import axiosInstance from '@/services/axiosInstance'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
const VAPID_PUBLIC_KEY = 'BFxWiuP7ktkwl13lJARYNsK9Z54eIMfJFf9GvehQr3egadRlqhvnhpuIf6G-TOEaTi_WyUn7YyFdMKYvAVstdaM'

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/** Subscribe to native (Capacitor) push notifications via FCM/APNs */
let nativeListenersAttached = false

async function subscribeNative(userId: string) {
  const { PushNotifications } = await import('@capacitor/push-notifications')
  const { FCM } = await import('@capacitor-community/fcm')

  let permStatus = await PushNotifications.checkPermissions()
  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions()
  }
  if (permStatus.receive !== 'granted') {
    console.warn('Push notification permission not granted')
    return
  }

  if (!nativeListenersAttached) {
    nativeListenersAttached = true

    // After APNs registration succeeds, get the FCM token from the plugin
    await PushNotifications.addListener('registration', async () => {
      try {
        const { token: fcmToken } = await FCM.getToken()
        const { useUserStore } = await import('@/stores/user/userStore')
        const uid = useUserStore().getUserId
        if (!uid) return
        await axiosInstance.post(`${API_BASE_URL}/users/notification-subscribe`, {
          fcmToken,
          userId: uid,
        })
      } catch (error) {
        console.error('Failed to get/send FCM token:', error)
      }
    })

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error)
    })
  }

  // Note: pushNotificationActionPerformed listener is registered in capacitor.ts
  // (runs early on every app start, including cold starts from notification tap)

  // Register with APNs — triggers 'registration' event, then we fetch FCM token
  await PushNotifications.register()
}

/** Subscribe to Web Push notifications (PWA / browser) */
async function subscribeWeb(userId: string) {
  if (!('serviceWorker' in navigator)) return

  const registration = await navigator.serviceWorker.ready
  const convertedVapidKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: convertedVapidKey,
  })

  await axiosInstance.post(`${API_BASE_URL}/users/notification-subscribe`, {
    subscription,
    userId,
  })
}

/** Main entry point — detects platform and subscribes accordingly */
export async function subscribeToPushNotifications(userId: string) {
  try {
    if (isNative) {
      await subscribeNative(userId)
    } else {
      await subscribeWeb(userId)
    }
  } catch (error) {
    console.error('Push notification subscription failed:', error)
  }
}
